import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { AppLayout, StickyFooter } from '@/components/layout';
import {
  DocumentSummaryBar,
  DocumentPreviewPanel,
  PageRangeCard,
  OrientationCopiesRow,
  ColorModeCard,
  AdvancedSettingsPanel,
  EcoNoticeCard,
  PrintOptionsStickyFooter,
  DetectingPageCountBanner,
  ManualPageCountCard,
} from '@/components/print-options';
import { usePrintCalculator } from '@/hooks/usePrintCalculator';
import { getApiErrorMessage } from '@/hooks/useApi';
import { printRequestService } from '@/api/services';
import { useSessionStore } from '@/store/useSessionStore';
import { ROUTES } from '@/constants/routes';
import { getFileTypeLabel } from '@/utils/fileValidation';
import { parseSimpleRange } from '@/utils/pageRange';
import type { PaymentPageState } from '@/types/payment.types';
import {
  toBackendPaperSize,
  toBackendOrientation,
  toBackendPageRange,
} from '@/utils/printRequestMapping';
import type { PrintJobConfig } from '@/types/index';
import type { CreatePrintRequestPayload } from '@/types';
import { COLOR_RATES, DUPLEX_DISCOUNT_RATE } from '@/constants/printOptions';
import { validateCustomRange } from '@/utils/customRangeValidation';

const DEFAULT_CONFIG: PrintJobConfig = {
  rangeMode: 'all',
  rangeExpression: '',
  fromPage: 1,
  toPage: 1,
  orientation: 'portrait',
  copies: 1,
  colorMode: 'bw',
  paperSizeId: 'a4',
  isDuplex: false,
  quality: 600,
  collate: true,
  staple: false,
};

export function PrintOptionsPage() {
  const navigate = useNavigate();
  const uploadedFile = useSessionStore((s) => s.uploadedFile);
  const documentMeta = useSessionStore((s) => s.documentMeta);
  const pageInfo = useSessionStore((s) => s.pageInfo);
  const isDetectingPageCount = useSessionStore((s) => s.isDetectingPageCount);
  const setPageInfo = useSessionStore((s) => s.setPageInfo);

  const [config, setConfig] = useState<PrintJobConfig>(DEFAULT_CONFIG);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const kiosk = useSessionStore((s) => s.kiosk);

  

  // No staged document — send the user back to Upload rather than showing
  // a page configured around a file that doesn't exist.
  useEffect(() => {
    if (!uploadedFile) {
      navigate(ROUTES.upload, { replace: true });
    }
  }, [uploadedFile, navigate]);

  // Server-confirmed page count (once a real upload API exists) always wins
  // over the client-side detection; until then, pageInfo carries the real
  // number for PDFs/images, or null while pending a manual entry.
  const totalPages = documentMeta?.pageCount ?? pageInfo.pageCount ?? 0;
  const pageCountKnown = totalPages > 0;

  // Once the real count resolves (or changes), default "All pages" bounds
  // to the full document instead of leaving the 1-1 placeholder in place.
  useEffect(() => {
    if (!pageCountKnown) return;
    setConfig((prev) => {
      const stillDefault = prev.toPage === 1 && prev.fromPage === 1 && !prev.rangeExpression;
      if (!stillDefault) return prev;
      return { ...prev, fromPage: 1, toPage: totalPages, rangeExpression: `1-${totalPages}` };
    });
  }, [totalPages, pageCountKnown]);

  // Show the final per-page price: the rate plus the merchant fee (e.g. 5 + 1 = 6), for both modes
  const bwRate = (kiosk?.bwRatePerPage ?? COLOR_RATES.bw) + (kiosk?.merchantFees || 0);
  const colorRate = (kiosk?.colorRatePerPage ?? COLOR_RATES.color) + (kiosk?.merchantFees || 0);
  const duplexDiscountRate = kiosk?.duplexExtraCharge ?? DUPLEX_DISCOUNT_RATE;

  const calculation = usePrintCalculator(config, totalPages, {
    bwRatePerPage: kiosk?.bwRatePerPage ?? null,
    colorRatePerPage: kiosk?.colorRatePerPage ?? null,
    duplexExtraCharge: kiosk?.duplexExtraCharge ?? null,
    merchantFees: kiosk?.merchantFees ?? null,
    convenienceFee: kiosk?.convenienceFee ?? null,
  });

  const isSinglePage = calculation.pageCount <= 1;

  // Duplex makes no sense for a single page — force it off so the price,
  // footer and payload never carry a stale duplex flag.
  useEffect(() => {
    if (isSinglePage && config.isDuplex) {
      setConfig((prev) => ({ ...prev, isDuplex: false }));
    }
  }, [isSinglePage, config.isDuplex]);  

  if (!uploadedFile) return null;

  // const updateConfig = <K extends keyof PrintJobConfig>(key: K, value: PrintJobConfig[K]) => {
  //   setConfig((prev) => ({ ...prev, [key]: value }));
  // };

  const updateConfig = <
    K extends keyof PrintJobConfig
  >(
    key: K,
    value: PrintJobConfig[K],
  ) => {
    setConfig((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

/**
 * Handles switching between:
 *
 * All Pages
 * Custom Set
 * Random Pages
 */
  const handleModeChange = (
    mode: PrintJobConfig['rangeMode'],
  ) => {
    setConfig((prev) => {
      if (mode === 'all') {
        return {
          ...prev,
          rangeMode: 'all',
          fromPage: 1,
          toPage: totalPages,
          rangeExpression: `1-${totalPages}`,
        };
      }

      if (mode === 'custom') {
        return {
          ...prev,
          rangeMode: 'custom',
          fromPage: 1,
          toPage: totalPages,
          rangeExpression: `1-${totalPages}`,
        };
      }

      // Random Pages
      return {
        ...prev,
        rangeMode: 'random',
        rangeExpression: '',
      };
    });
  };


  // The mobile from/to stepper and the desktop free-text expression edit
  // the same underlying "custom" range — keep them in sync so
  // pageCountForConfig always has one consistent source of truth.
  // const handleFromPageChange = (value: number) => {
  //   setConfig((prev) => ({ ...prev, fromPage: value, rangeExpression: `${value}-${prev.toPage}` }));
  // };
  const handleFromPageChange = (value: number) => {
    setConfig((prev) => ({
      ...prev,
      fromPage: value,
      rangeExpression: `${value}-${prev.toPage}`,
    }));
  };

  // const handleToPageChange = (value: number) => {
  //   setConfig((prev) => ({ ...prev, toPage: value, rangeExpression: `${prev.fromPage}-${value}` }));
  // };
  const handleToPageChange = (value: number) => {
    setConfig((prev) => ({
      ...prev,
      toPage: value,
      rangeExpression: `${prev.fromPage}-${value}`,
    }));
  };
 

  const handleRangeExpressionChange = (value: string) => {
    if (config.rangeMode === 'custom') {
      // Strip everything except digits and hyphen (blocks commas, spaces, letters)
      const cleaned = value.replace(/[^\d-]/g, '');
      const match = /^(\d+)-(\d+)$/.exec(cleaned);

      setConfig((prev) => ({
        ...prev,
        rangeExpression: cleaned,
        fromPage: match ? Number(match[1]) : prev.fromPage,
        toPage: match ? Number(match[2]) : prev.toPage,
      }));
      return;
    }

    // Random mode: keep the existing behaviour
    const simple = parseSimpleRange(value);
    setConfig((prev) => ({
      ...prev,
      rangeExpression: value,
      fromPage: simple?.from ?? prev.fromPage,
      toPage: simple?.to ?? prev.toPage,
    }));
  };

  const handleConfirmManualPageCount = (count: number) => {
    setPageInfo({ pageCount: count, source: 'manual' });
  };

  const customRangeError =
  config.rangeMode === 'custom'
    ? validateCustomRange(config.rangeExpression, totalPages)
    : null;

  const handleSubmit = async () => {
    if (customRangeError) {
      toast.error(customRangeError);
      return;
    }
    setIsSubmitting(true); 

    const payload: CreatePrintRequestPayload = {
      file: uploadedFile,
      original_filename: uploadedFile.name,
      total_pages: totalPages,
      page_range: toBackendPageRange(config),
      copies: config.copies,
      is_color: config.colorMode === 'color',
      is_duplex: config.isDuplex && !isSinglePage,
      paper_size: toBackendPaperSize(config.paperSizeId),
      orientation: toBackendOrientation(config.orientation),
      total_amount: calculation.total,
      // TODO: confirm these field names against the real shape of `kiosk`
      // in useSessionStore — assumed kiosk.id and kiosk.vendorId here.
      vendor: kiosk?.vendorId ?? 0,
      kiosk: Number(kiosk?.kioskId ?? 0),
      user_name: '',
      user_phone: '',
      user_email: '',
    };

    try {
      const response = await printRequestService.create(payload); 
      console.log('response',response);
      const printRequest =  response.data.data;
      console.log('printrequest',printRequest);
      navigate(ROUTES.payment, {
        state: {
          printRequestId: printRequest.id,
          vendorId: kiosk?.vendorId ?? 0,
          kioskId: Number(kiosk?.kioskId ?? 0),
          fileName: uploadedFile.name,
          colorMode: config.colorMode,
          paperSizeId: config.paperSizeId,
          isDuplex: config.isDuplex,
          copies: config.copies,
          pageCount: calculation.pageCount,
          totalImpressions: calculation.impressions,
          basePrice: calculation.basePrice,
          ecoDiscount: calculation.ecoDiscount,
          stapleFee: calculation.stapleFee,
          convenienceFee: calculation.convenienceFee,
          merchantFeeTotal: calculation.merchantFeeTotal,
          // the server computes the real amount; show exactly what it will charge
          total: Number(printRequest.total_amount) || calculation.total,
          rate: config.colorMode === 'color' ? colorRate : bwRate,
        } satisfies PaymentPageState,
      });
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Could not submit your print request. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppLayout title="Print options" currentStep={2}>
      <DocumentSummaryBar
        fileName={uploadedFile.name}
        fileSizeBytes={uploadedFile.size}
        pageCount={totalPages}
        onChangeDocument={() => navigate(ROUTES.upload)}
      />

      {!pageCountKnown ? (
        // Either still counting a PDF, or waiting on a manual page count
        // for a format the browser can't parse (DOC/DOCX/PPT/PPTX).
        isDetectingPageCount ? (
          <DetectingPageCountBanner />
        ) : (
          <ManualPageCountCard
            fileTypeLabel={getFileTypeLabel(uploadedFile.type)}
            onConfirm={handleConfirmManualPageCount}
          />
        )
      ) : (
        <>
          <Row className="g-3 g-lg-4 mt-0">
            <Col xs={12} lg={7} className="d-flex flex-column gap-3 gap-lg-4">
              <DocumentPreviewPanel
                file={uploadedFile}
                fileName={uploadedFile.name}
                fileSizeBytes={uploadedFile.size}
                totalPages={totalPages}
                onChangeDocument={() => navigate(ROUTES.upload)}
              />
              <PageRangeCard
                totalPages={totalPages}
                mode={config.rangeMode}
                fromPage={config.fromPage}
                toPage={config.toPage}
                rangeExpression={config.rangeExpression}
                pageCount={calculation.pageCount}
                onModeChange={handleModeChange}
                onFromPageChange={handleFromPageChange}
                onToPageChange={handleToPageChange}
                onRangeExpressionChange={handleRangeExpressionChange}
                rangeError={customRangeError}
              />
              <OrientationCopiesRow
                orientation={config.orientation}
                copies={config.copies}
                onOrientationChange={(value) => updateConfig('orientation', value)}
                onCopiesChange={(value) => updateConfig('copies', value)}
              />
            </Col>

            <Col xs={12} lg={5} className="d-flex flex-column gap-3 gap-lg-4">
              <ColorModeCard
                value={config.colorMode}
                supportsColor={kiosk?.supportsColor ?? false}
                  bwRate={bwRate}
                  colorRate={colorRate}
                onChange={(value) => updateConfig('colorMode', value)}
              />
              <AdvancedSettingsPanel
                paperSizeId={config.paperSizeId}
                isDuplex={config.isDuplex}
                quality={config.quality}
                collate={config.collate}
                staple={config.staple}
                supportsDuplex={kiosk?.supportsDuplex ?? false}
                supportsA4={kiosk?.supportsA4 ?? false}
                supportsLetter={kiosk?.supportsLetter ?? false}
                supportsLegal={kiosk?.supportsLegal ?? false}
                onPaperSizeChange={(value) => updateConfig('paperSizeId', value)}
                onDuplexChange={(value) => updateConfig('isDuplex', value)}
                onQualityChange={(value) => updateConfig('quality', value)}
                onCollateChange={(value) => updateConfig('collate', value)}
                onStapleChange={(value) => updateConfig('staple', value)}
                duplexDiscountRate={duplexDiscountRate} 
                duplexDisabled={isSinglePage}
                duplexDisabledReason="Not available for a single page"
              />
              <EcoNoticeCard />
            </Col>
          </Row>

          <StickyFooter>
            <PrintOptionsStickyFooter
              config={config}
              calculation={calculation}
              isSubmitting={isSubmitting}
              onSubmit={handleSubmit}
              bwRate={bwRate}
              colorRate={colorRate}
              disabled={!!customRangeError}
            />
          </StickyFooter>
        </>
      )}
    </AppLayout>
  );
}